import BOSDateInput from 'Components/Generic/BOSDateInput';
import OrganizationSelect from 'Components/Generic/BOSFilterSelect/OrganizationSelect';
import BOSSelect from 'Components/Generic/BOSSelect';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import ChargeAccountData from 'Data/ChargeAccountData';
import { useAlertContext } from 'Data/context/Alerts';
import { getOrgNameFromId, getPIForOrg } from 'Data/context/Organizations';
import { user_roles } from 'Data/Enum';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { TRANSACTION_CATEGORY } from 'Pages/Transactions/Enum';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { isUserAuthorized } from 'Util';
import { formatDate } from 'Util/dateUtil';

let DM = new DataManager();

const AccountDetails = ({ accountId, accountObject, params, ...props }) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const {
    currentObject: account,
    hasBeenUpdated,
    reset: resetAccount,
    updateField,
    updateFields,
    originalObject: originalAccount,
    doSave,
  } = useUpdatableObject(
    ChargeAccountData.updateAccount,
    ChargeAccountData.createAccount,
  );
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalAccount,
    undoFunction: ChargeAccountData.updateAccount,
    onUndo: (originalAcct) => getAccount(originalAcct.id),
    objectType: 'Account',
  });

  let editable = isUserAuthorized({
    roles: [
      user_roles.ADMINISTRATOR,
      // [account.parent_organization_id, user_org_roles.MANAGER.key],
    ],
  });

  useEffect(() => {
    DM.add(
      ChargeAccountData.registerListener(
        'BLANK_RECORD_LOADED',
        onAccountLoaded,
      ),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORD_LOADED', onAccountLoaded),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORD_UPDATED', onAccountUpdated),
      ChargeAccountData,
    );
    DM.add(
      ChargeAccountData.registerListener('RECORDS_DELETED', onAccountDeleted),
      ChargeAccountData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!accountId && !params?.id) {
      ChargeAccountData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (accountId > 0) {
      getAccount(accountId);
    }
  }, [accountId]);

  useEffect(() => {
    if (params?.id) {
      getAccount(params.id);
    }
  }, [params]);

  useEffect(() => {
    if (!editable) {
      resetAccount(accountObject);
    } else if (accountObject?.id && Number(accountObject.id) >= 0) {
      getAccount(accountObject.id);
    }
  }, [accountObject]);

  const getAccount = (id) => {
    ChargeAccountData.getRecord(id);
  };

  const onAccountLoaded = (payload) => {
    if (payload) {
      resetAccount(payload);
      resetUndo(() => getAccount(payload.id));
    }
  };

  const updateOrg = (orgId) => {
    if (!orgId) {
      updateFields({ parent_organization_id: null, pi_id: null });
    }
    let pi_id = getPIForOrg(orgId);
    updateFields({ parent_organization_id: orgId, pi_id });
  };

  const onAccountUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  let delCancelButton =
    editable &&
    (accountId ? (
      <DeleteButton
        onDeleteItem={() => {
          deleteClicked();
        }}
        tooltipText={'Delete Account'}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const deleteAccount = () => {
    ChargeAccountData.deleteAccount(account);
  };

  const deleteClicked = () => {
    openDeleteDialog({
      objectType: 'Account',
      onConfirmDelete: deleteAccount,
    });
  };

  const onAccountDeleted = () => {
    showDeletedToast('Account');
  };

  let maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 20);

  let detailData = [
    {
      label: 'Category',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSSelect
              defaultSelectedId={account?.category}
              onItemSelect={(category) => {
                updateField('category', category?.key || null);
              }}
              listData={Object.entries(TRANSACTION_CATEGORY).map(
                ([key, value]) => {
                  return {
                    key,
                    value,
                  };
                },
              )}
              idField={'key'}
              labelField={'value'}
            />
          ) : account?.category ? (
            TRANSACTION_CATEGORY[account.category]
          ) : (
            ''
          );
        },
      },
    },
    {
      label: 'Core Facility',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <OrganizationSelect
              key={account.parent_organization_id}
              defaultSelectedId={account.parent_organization_id}
              onSelectOrganization={(org) => updateOrg(org?.id || null)}
            />
          ) : (
            getOrgNameFromId(account.parent_organization_id)
          );
        },
      },
    },
    {
      label: 'Name',
      value: account.name,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('name', newValue);
      },
      isRequired: true,
    },
    {
      label: 'Short Name',
      value: account.short_name,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('short_name', newValue);
      },
    },
    {
      label: 'Org',
      value: account.org,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('org', newValue);
      },
      isRequired: true,
    },
    {
      label: 'Fund',
      value: account.fund,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('fund', newValue);
      },
      isRequired: true,
    },
    {
      label: 'Act / Project',
      value: account.code,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('code', newValue);
      },
      isRequired: true,
    },
    {
      label: 'Valid From',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSDateInput
              value={account.valid_from}
              onChange={(date) => updateField('valid_from', date)}
            />
          ) : (
            formatDate(account.valid_from)
          );
        },
      },
    },
    {
      label: 'Valid To',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSDateInput
              value={account.valid_to}
              onChange={(date) => updateField('valid_to', date)}
              maxDate={maxDate}
            />
          ) : (
            formatDate(account.valid_to)
          );
        },
      },
    },
    {
      label: 'Grant Number',
      value: account.grant_number,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('grant_number', newValue);
      },
    },
    {
      label: 'Notes',
      value: account.notes,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('notes', newValue);
      },
    },
    // {
    //   label: 'Status',
    //   value: account.status,
    // },
  ];

  const undoButton = editable && account.id && hasBeenUpdated && (
    <UndoButton clickHandler={() => getAccount(account.id)} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    account && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 600,
        }}>
        <EditableDetailPanel
          editable={editable}
          renderChild={(inEditState) => {
            return <DetailTable data={detailData} editable={inEditState} />;
          }}
          footer={footer}
          {...props}
        />
        {delCancelButton}
      </div>
    )
  );
};

AccountDetails.propTypes = {
  accountId: PropTypes.number,
  accountObject: PropTypes.object,
  params: PropTypes.shape({ id: PropTypes.string }),
};

export default AccountDetails;
